/**
 * src/components/users/AddUserModal.tsx
 * Admin modal for creating and updating user accounts with Full Name,
 * WhatsApp number, Google Pay UPI ID, and Profile Photo customization.
 */

import React, { useState, useEffect, useRef } from 'react';
import { UserPlus, Edit2, Camera, Upload, Trash2, Smartphone, Sparkles, Eye, EyeOff } from 'lucide-react';
import { GlassModal } from '../ui/GlassModal';
import { GlassInput } from '../ui/GlassInput';
import { GlassButton } from '../ui/GlassButton';
import type { UserItem } from '../../services/api';

interface AddUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: any) => Promise<any>;
  editUser?: UserItem | null;
}

const EMOJI_PRESETS = [
  { emoji: '🦊', grad: ['#ff9500', '#ff3b30'] },
  { emoji: '🦁', grad: ['#ffcc00', '#ff9500'] },
  { emoji: '👑', grad: ['#af52de', '#ff2d55'] },
  { emoji: '🚀', grad: ['#007aff', '#5856d6'] },
  { emoji: '💎', grad: ['#5ac8fa', '#007aff'] }
];

const createEmojiAvatarUri = (emoji: string, grad: string[]) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 120 120">
    <defs>
      <linearGradient id="bg" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="${grad[0]}" />
        <stop offset="100%" stop-color="${grad[1]}" />
      </linearGradient>
    </defs>
    <rect width="120" height="120" rx="36" fill="url(#bg)" />
    <text x="50%" y="54%" font-size="64" dominant-baseline="central" text-anchor="middle">${emoji}</text>
  </svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

export const AddUserModal: React.FC<AddUserModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  editUser
}) => {
  const isEditing = Boolean(editUser);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [username, setUsername] = useState('');
  const [fullName, setFullName] = useState('');
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [role, setRole] = useState<'admin' | 'user'>('user');
  const [isActive, setIsActive] = useState<boolean>(true);
  const [upiId, setUpiId] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [initialBalance, setInitialBalance] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    if (editUser) {
      setUsername(editUser.username || '');
      setFullName(editUser.full_name || '');
      setWhatsappPhone(editUser.whatsapp_phone || '');
      setRole(editUser.role || 'user');
      setIsActive(editUser.is_active !== 0);
      setUpiId(editUser.upi_id || '');
      setAvatarUrl(editUser.avatar_url || null);
      setPassword(''); // Blank unless resetting
      setInitialBalance('');
    } else {
      setUsername('');
      setFullName('');
      setWhatsappPhone('');
      setPassword('');
      setRole('user');
      setIsActive(true);
      setUpiId('');
      setAvatarUrl(null);
      setInitialBalance('0.00');
    }
    setFormError('');
  }, [editUser, isOpen]);

  // Handle Photo File Upload
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setFormError('Please select a valid image file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const MAX_SIZE = 200;
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_SIZE) {
              height = Math.round((height * MAX_SIZE) / width);
              width = MAX_SIZE;
            }
          } else {
            if (height > MAX_SIZE) {
              width = Math.round((width * MAX_SIZE) / height);
              height = MAX_SIZE;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) throw new Error('Canvas unavailable');

          ctx.drawImage(img, 0, 0, width, height);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
          setAvatarUrl(dataUrl);
        } catch {
          setFormError('Failed to process image');
        }
      };
      img.src = event.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (username.trim().length < 2) {
      setFormError('Username must be at least 2 characters.');
      return;
    }

    if (!isEditing && password.length < 4) {
      setFormError('Password must be at least 4 characters.');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: any = {
        username: username.trim(),
        full_name: fullName.trim() || null,
        whatsapp_phone: whatsappPhone.trim() || null,
        avatar_url: avatarUrl || null,
        role,
        is_active: isActive ? 1 : 0,
        upi_id: upiId.trim() || null
      };

      if (!isEditing) {
        payload.password = password;
        payload.initial_balance = parseFloat(initialBalance) || 0;
      } else if (password.trim()) {
        payload.password = password.trim();
      }

      await onSubmit(payload);
      onClose();
    } catch (err: any) {
      setFormError(err.message || 'Failed to save user.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? `Edit User: ${fullName || editUser?.username}` : 'Create New User Account'}
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {formError && (
          <div
            style={{
              padding: '12px 14px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--crimson-tint)',
              border: '1px solid rgba(244, 63, 94, 0.3)',
              color: 'var(--crimson-light)',
              fontSize: '0.84rem'
            }}
          >
            {formError}
          </div>
        )}

        {/* Hidden File Input for Avatar Photo */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handlePhotoUpload}
          accept="image/*"
          style={{ display: 'none' }}
        />

        {/* Profile Photo Uploader Section */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          padding: '12px 14px',
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.04)',
          border: 'var(--border-hairline)'
        }}>
          <div style={{ position: 'relative' }}>
            <div
              onClick={() => fileInputRef.current?.click()}
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '20px',
                background: 'linear-gradient(135deg, #0a84ff 0%, #0056b3 100%)',
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '1.6rem',
                fontWeight: 800,
                cursor: 'pointer',
                overflow: 'hidden',
                boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                border: '1.5px solid rgba(255, 255, 255, 0.2)'
              }}
              title="Click to upload profile photo"
            >
              {avatarUrl ? (
                <img src={avatarUrl} alt="User Avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                (fullName || username || 'U').charAt(0).toUpperCase()
              )}
            </div>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              style={{
                position: 'absolute',
                bottom: '-4px',
                right: '-4px',
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: 'var(--surface-primary)',
                border: '1.5px solid var(--ios-blue)',
                color: 'var(--ios-blue)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer'
              }}
              title="Upload photo"
            >
              <Camera size={12} strokeWidth={2.4} />
            </button>
          </div>

          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '0.84rem', fontWeight: 700, color: 'var(--text-primary)' }}>
              Member Profile Photo
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: '2px' }}>
              Upload custom photo or pick an Apple Memoji
            </div>

            {/* Quick Memoji Presets */}
            <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
              {EMOJI_PRESETS.map((p, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setAvatarUrl(createEmojiAvatarUri(p.emoji, p.grad))}
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '8px',
                    border: 'none',
                    background: `linear-gradient(135deg, ${p.grad[0]} 0%, ${p.grad[1]} 100%)`,
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title="Apply avatar"
                >
                  {p.emoji}
                </button>
              ))}

              {avatarUrl && (
                <button
                  type="button"
                  onClick={() => setAvatarUrl(null)}
                  style={{
                    padding: '2px 8px',
                    borderRadius: '8px',
                    border: 'var(--border-hairline)',
                    background: 'rgba(255, 69, 58, 0.12)',
                    color: 'var(--ios-red)',
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <Trash2 size={11} />
                  <span>Reset</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Full Name & Username */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <GlassInput
            label="Full Name"
            placeholder="e.g. Rahul Sharma"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            autoFocus
          />

          <GlassInput
            label="Login Username"
            placeholder="e.g. rahul"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
          />
        </div>

        {/* WhatsApp Phone & Google Pay UPI ID */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <GlassInput
            label="WhatsApp Number (Optional)"
            placeholder="e.g. 9876543210"
            value={whatsappPhone}
            onChange={(e) => setWhatsappPhone(e.target.value)}
          />

          <GlassInput
            label="Google Pay UPI ID (Optional)"
            placeholder="e.g. 9876543210@okaxis"
            value={upiId}
            onChange={(e) => setUpiId(e.target.value)}
          />
        </div>

        {/* Password & Change Password Section */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
            <label style={{ fontSize: '0.84rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
              {isEditing ? 'Change Password (Leave blank to keep current)' : 'Login Password'}
            </label>
            <button
              type="button"
              onClick={() => {
                const prefixes = ['CKPass', 'Smart', 'Ledger', 'Audit', 'Secure'];
                const randomPrefix = prefixes[Math.floor(Math.random() * prefixes.length)];
                const randomDigits = Math.floor(1000 + Math.random() * 9000);
                const symbols = ['@', '#', '!', '$'];
                const randomSymbol = symbols[Math.floor(Math.random() * symbols.length)];
                const generated = `${randomPrefix}${randomSymbol}${randomDigits}`;
                setPassword(generated);
                setShowPassword(true);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--ios-blue)',
                fontSize: '0.74rem',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}
              title="Suggest strong password"
            >
              <Sparkles size={12} />
              <span>Suggest Password</span>
            </button>
          </div>

          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              className="glass-input"
              style={{ width: '100%', paddingRight: '40px', fontSize: '0.9rem', borderRadius: '12px' }}
              placeholder={isEditing ? 'Enter new password to change' : '••••••••'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required={!isEditing}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              style={{
                position: 'absolute',
                right: '10px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'transparent',
                border: 'none',
                color: 'var(--text-tertiary)',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center'
              }}
              title={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {isEditing && password.trim().length > 0 && (
            <div style={{ fontSize: '0.72rem', color: 'var(--ios-green)', marginTop: '4px', fontWeight: 600 }}>
              ✓ New password will be set upon saving
            </div>
          )}
        </div>

        {/* Role Selector */}
        <div>
          <label style={{ fontSize: '0.84rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
            Account Role
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
            <button
              type="button"
              onClick={() => setRole('user')}
              style={{
                padding: '10px',
                borderRadius: 'var(--radius-md)',
                border: role === 'user' ? '2px solid var(--indigo)' : '1px solid var(--border-subtle)',
                background: role === 'user' ? 'var(--indigo-tint)' : 'var(--surface-glass)',
                color: role === 'user' ? 'var(--indigo-light)' : 'var(--text-secondary)',
                fontWeight: 700,
                fontSize: '0.84rem',
                cursor: 'pointer'
              }}
            >
              Regular User
            </button>

            <button
              type="button"
              onClick={() => setRole('admin')}
              style={{
                padding: '10px',
                borderRadius: 'var(--radius-md)',
                border: role === 'admin' ? '2px solid var(--indigo)' : '1px solid var(--border-subtle)',
                background: role === 'admin' ? 'var(--indigo-tint)' : 'var(--surface-glass)',
                color: role === 'admin' ? 'var(--indigo-light)' : 'var(--text-secondary)',
                fontWeight: 700,
                fontSize: '0.84rem',
                cursor: 'pointer'
              }}
            >
              Administrator
            </button>
          </div>
        </div>

        {/* Account Status Selector */}
        <div>
          <label style={{ fontSize: '0.84rem', fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: '8px' }}>
            Account Status
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px' }}>
            <button
              type="button"
              onClick={() => setIsActive(true)}
              style={{
                padding: '8px 14px',
                borderRadius: 'var(--radius-button)',
                border: isActive ? '1.5px solid var(--ios-green)' : 'var(--border-hairline)',
                background: isActive ? 'rgba(48, 209, 88, 0.16)' : 'rgba(118, 118, 128, 0.14)',
                color: isActive ? 'var(--ios-green)' : 'var(--text-secondary)',
                fontWeight: 700,
                fontSize: '0.84rem',
                cursor: 'pointer'
              }}
            >
              ✓ Active
            </button>
            <button
              type="button"
              onClick={() => setIsActive(false)}
              style={{
                padding: '8px 14px',
                borderRadius: 'var(--radius-button)',
                border: !isActive ? '1.5px solid var(--ios-red)' : 'var(--border-hairline)',
                background: !isActive ? 'rgba(255, 69, 58, 0.16)' : 'rgba(118, 118, 128, 0.14)',
                color: !isActive ? 'var(--ios-red)' : 'var(--text-secondary)',
                fontWeight: 700,
                fontSize: '0.84rem',
                cursor: 'pointer'
              }}
            >
              Deactivated
            </button>
          </div>
        </div>

        {/* Initial Balance (Only for new accounts) */}
        {!isEditing && (
          <GlassInput
            label="Initial Balance (₹)"
            type="number"
            step="0.01"
            placeholder="0.00"
            value={initialBalance}
            onChange={(e) => setInitialBalance(e.target.value)}
          />
        )}

        {/* Submit Buttons */}
        <div style={{ display: 'flex', gap: '12px', marginTop: '10px' }}>
          <GlassButton
            type="button"
            variant="ghost"
            onClick={onClose}
            disabled={isSubmitting}
            style={{ flex: 1 }}
          >
            Cancel
          </GlassButton>
          <GlassButton
            type="submit"
            variant="primary"
            disabled={isSubmitting}
            style={{ flex: 1 }}
          >
            {isSubmitting ? 'Saving...' : isEditing ? 'Save Changes' : 'Create User'}
          </GlassButton>
        </div>
      </form>
    </GlassModal>
  );
};
